import type { ValidationRule } from "./types";
import { isValidEmail } from "./email";

// ── Private validation helpers ──────────────────────────────

const UNICODE_LETTER_REGEX = /\p{L}/u;
const HTTPS_SCHEME_REGEX = /^https:\/\//i;

// ── Builders ────────────────────────────────────────────────

/**
 * Fails when value exceeds the given character limit.
 * `formatMessage` builds the message from `max` — callers doing i18n should
 * interpolate via Lingui (e.g. a `t` macro) rather than string concatenation,
 * so plural/number formatting stays correct per locale.
 */
export function maxLength(
  max: number,
  formatMessage: (max: number) => string,
): ValidationRule {
  return {
    match: (value) => value.length > max,
    message: formatMessage(max),
  };
}

/**
 * Fails when the trimmed value contains no Unicode letters.
 * Empty values pass — defer to the required validator.
 */
export function nameFormat(message: string): ValidationRule {
  return {
    match: (value) => {
      const trimmed = value.trim();
      if (trimmed.length === 0) return false;
      return !UNICODE_LETTER_REGEX.test(trimmed);
    },
    message,
  };
}

/**
 * Fails when a non-empty value doesn't match the email regex.
 * Empty values pass — defer to the required validator.
 */

/**
 * @todo confirm with Ian that this validator is actually needed
 * the browser implementation seems more verbose.
 */

export function emailFormat(message: string): ValidationRule {
  return {
    match: (value) => value.trim().length > 0 && !isValidEmail(value),
    message,
  };
}

/**
 * Fails when a non-empty value doesn't start with `https://`.
 * Empty values pass — defer to the required validator.
 */
export function httpsUrlFormat(
  message = "Must be a secure URL starting with https://",
): ValidationRule {
  return {
    match: (value) => {
      const trimmed = value.trim();
      return trimmed.length > 0 && !HTTPS_SCHEME_REGEX.test(trimmed);
    },
    message,
  };
}
